'use client';

import { useCallback } from 'react';
import { toasterApi } from '@terra/ui-design-system';
import {
  useAppTargetId,
  useTargetId,
  useTargetType,
} from '../../../store/projectStore';
import { useMainContentTabs } from '../../../store/layoutStore';
import { useExecutionsStore } from '../../../store/executionsStore';
import { useThrottledCallback } from '../../../hooks/useThrottledCallback';
import { useSmokeTestStatusSSE } from '../../../hooks/useSmokeTestStatusSSE';
import { useSignalAssignmentSSE } from '../../../hooks/useSignalAssignmentSSE';
import { useSignalCreatedSSE } from '../../../hooks/useSignalCreatedSSE';
import { useNeoAttackSSE } from '../../../hooks/useNeoAttackSSE';
import { useNeoAttackReconciliation } from '../../../hooks/useNeoAttackReconciliation';
import { useSignalsQueryInvalidation } from './useSignalsQueryInvalidation';

const NEO_ATTACK_STATE_CLEAR_DELAY_MS = 2000;
const SIGNALS_INVALIDATION_THROTTLE_MS = 750;

export function useSignalsLiveUpdates() {
  const targetId = useTargetId();
  const targetType = useTargetType();
  const appTargetId = useAppTargetId();
  const { navigateInActiveTab } = useMainContentTabs();
  const removeNeoAttack = useExecutionsStore((state) => state.removeNeoAttack);
  const { invalidateSignalList, invalidateSignalCounts } =
    useSignalsQueryInvalidation();
  const invalidateSignalListThrottled = useThrottledCallback(
    invalidateSignalList,
    SIGNALS_INVALIDATION_THROTTLE_MS,
  );
  const handleNeoAttackComplete = useCallback(
    ({ signalId, threadId }: { signalId: string; threadId: string }) => {
      toasterApi.success({
        title: 'Neo completed',
        description: 'Click to view the trace',
        action: {
          label: 'View Trace',
          onClick: () => {
            navigateInActiveTab({
              id: `executions-${threadId}`,
              title: 'Execution',
              type: 'executions',
              data: {
                threadId,
                agentName: 'neo',
              },
            });
          },
        },
      });
      setTimeout(() => {
        removeNeoAttack(signalId);
      }, NEO_ATTACK_STATE_CLEAR_DELAY_MS);
      invalidateSignalList();
      invalidateSignalCounts();
    },
    [
      invalidateSignalCounts,
      invalidateSignalList,
      navigateInActiveTab,
      removeNeoAttack,
    ],
  );
  const handleNeoTraceEvent = useCallback(() => {
    invalidateSignalListThrottled();
  }, [invalidateSignalListThrottled]);
  const isExternalNetworkTarget = targetType === 'EXTERNAL_NETWORK';

  useSmokeTestStatusSSE({ applicationId: appTargetId });
  useSignalAssignmentSSE({ applicationId: appTargetId });
  useSignalCreatedSSE({
    applicationId: appTargetId,
    externalSurfaceId: isExternalNetworkTarget ? targetId : undefined,
    listenForExternalNetworkSignals: isExternalNetworkTarget,
  });
  useNeoAttackSSE({
    applicationId: appTargetId,
    onTraceEvent: handleNeoTraceEvent,
    onNeoAttackComplete: handleNeoAttackComplete,
  });
  useNeoAttackReconciliation({
    applicationId: appTargetId,
    enabled: !!appTargetId,
  });
}
